import clsx from 'clsx';
import {
  Column,
  selectableColumn,
  showConfirmDialog,
  showMessageBox,
} from '../../../../components';
import { IconButton } from '../../../../components/controls';
import { useStore } from '../../../../store';
import PulseKey from '../../store/PulseKey';
import PulseKeySearch from '../../store/PulseKeySearch';
import classes from './createColumns.module.scss';

export default function createColumns(
  search: PulseKeySearch,
): Column<PulseKey>[] {
  return [
    selectableColumn(),
    {
      header: 'Pulse Key',
      id: 'key',
      renderCell(pulseKey, column) {
        return (
          <td className={clsx(classes.keyCell, column.classes)} key={column.id}>
            {pulseKey.key}
            <IconButton
              className={classes.copyKey}
              color="default"
              icon="copy"
              onClick={pulseKey.copyToClipboard}
              size="small"
              title="Copy the key to the clipboard"
            />
          </td>
        );
      },
    },
    {
      header: 'Product',
      id: 'sellableId',
      renderCell(pulseKey) {
        return search.products.getProductDisplayName(pulseKey.sellableId);
      },
    },
    {
      header: 'SKU',
      id: 'sku' as any,
      renderCell(pulseKey) {
        return search.products.getProductSku(pulseKey.sellableId);
      },
      visible: search.columns.sku,
    },
    {
      header: 'Creation date',
      id: 'createdAt',
      format: 'time',
      visible: search.columns.createdAt,
    },
    {
      header: 'Customer',
      id: 'customerEmail',
      orderBy: 'redemptionEmail',
    },
    {
      header: 'Redemption Time',
      id: 'redeemedAt',
      format: 'date',
    },
    {
      header: 'Status',
      id: 'status',
      visible: search.columns.status,
    },
    {
      header: 'Label',
      id: 'label',
      visible: search.columns.label,
    },
    {
      header: 'Number of Downloads',
      id: 'downloadCount',
      visible: search.columns.downloadCount,
    },
    {
      header: 'IP of redemption',
      id: 'redemptionIp',
      orderBy: false,
      visible: search.columns.redemptionIp,
    },
    {
      header: 'IP of download',
      id: 'ipOfDownload',
      orderBy: false,
      visible: search.columns.ipOfDownload,
    },
    {
      header: 'Sent date',
      id: 'fulfilledAt',
      visible: search.columns.fulfilledAt,
      format: 'date',
    },
    {
      header: '',
      id: 'actions' as any,
      orderBy: false,
      renderCell(pulseKey, column) {
        return (
          <td
            className={clsx(classes.actionCell, column.classes)}
            key={column.id}
          >
            <ResetDownloadCountButton pulseKey={pulseKey} search={search} />
            <ToggleDisableButton pulseKey={pulseKey} search={search} />
            <UnassignButton pulseKey={pulseKey} search={search} />
            <ChangeCustomerButton pulseKey={pulseKey} search={search} />
            <SendPulseKeyButton pulseKey={pulseKey} />
          </td>
        );
      },
    },
  ];
}

function UnassignButton({
  pulseKey,
  search,
}: {
  pulseKey: PulseKey;
  search: PulseKeySearch;
}) {
  const { pulseKeyStore } = useStore();

  async function onClick() {
    await pulseKeyStore.unassignKey(pulseKey);
    await search.search();
  }

  return (
    <IconButton
      icon="user-times"
      onClick={onClick}
      size="small"
      title="Revoke customer assignment"
    />
  );
}

function ToggleDisableButton({
  pulseKey,
  search,
}: {
  pulseKey: PulseKey;
  search: PulseKeySearch;
}) {
  const { pulseKeyStore } = useStore();
  const { disabled } = pulseKey;

  async function onEnableClick() {
    await pulseKeyStore.enableKey(pulseKey);
    await search.search();
  }

  async function onDisableClick() {
    await pulseKeyStore.disableKey(pulseKey);
    await search.search();
  }

  if (disabled) {
    return (
      <IconButton
        icon="check-circle"
        onClick={onEnableClick}
        size="small"
        title="Enable key. A user will be able to use it again."
      />
    );
  }

  return (
    <IconButton
      icon="ban"
      onClick={onDisableClick}
      size="small"
      title="Disable key. Disabled key can't be redeemed."
    />
  );
}

function ResetDownloadCountButton({
  pulseKey,
  search,
}: {
  pulseKey: PulseKey;
  search: PulseKeySearch;
}) {
  const { pulseKeyStore } = useStore();

  async function onClick() {
    await pulseKeyStore.resetKeyDownloadCount(pulseKey);
    await search.search();
  }

  return (
    <IconButton
      icon="creative-commons-zero"
      onClick={onClick}
      size="small"
      title="Reset download count"
    />
  );
}

function ChangeCustomerButton({
  pulseKey,
  search,
}: {
  pulseKey: PulseKey;
  search: PulseKeySearch;
}) {
  const { pulseKeyStore } = useStore();

  async function onClick() {
    const email = await showConfirmDialog({
      title: 'Change customer',
      textField: {
        label: 'Email of a new customer',
      },
    });

    if (email !== 'cancel') {
      await pulseKeyStore.changeKeyCustomer(pulseKey, email);
      await search.search();
    }
  }

  return (
    <IconButton
      icon="exchange-alt"
      onClick={onClick}
      size="small"
      title="Assign key to another customer"
    />
  );
}

function SendPulseKeyButton({ pulseKey }: { pulseKey: PulseKey }) {
  const { pulseKeyStore } = useStore();

  async function onClick() {
    const buttonId = await showMessageBox({
      buttons: [
        { id: 'ok', title: 'send' },
        { id: 'cancel', title: 'cancel' },
      ],
      content:
        `Are you sure you want to send the key ` +
        `${pulseKey.key} to ${pulseKey.customerEmail}?`,
      title: 'Pulse Key send',
    });

    if (buttonId === 'ok') {
      return pulseKeyStore.sendKeyToTheCustomer(pulseKey);
    }

    return null;
  }

  return (
    <IconButton
      icon="envelope"
      onClick={onClick}
      size="small"
      title="Send Pulse Key to the customer"
    />
  );
}
